import Link from 'next/link'
import { useTranslation } from 'react-i18next'

import CommunityStatsBar from '@/components/atomic/molecules/CommunityStatsBar'
import { NONMEMBER_ID } from '@/common/constants'
import { postAmplitude } from '@/core/lib'
import { AirbridgeEvents, sendAirbridgeEvent } from '@/utils/airbridge'
import { generateTagId } from '@/utils/test-automation'
import { GetCommentsByDoctorContent } from '@/features/community/api/document/community-document.types'
import { DoctorDetailAmplitudeType } from '@/features/doctors/[doctorId]/index/helpers/types/event-types'

interface Props {
  comment: GetCommentsByDoctorContent
  commentIndex: number
  userId: number
  commonAmplitudeProps: DoctorDetailAmplitudeType
  onClickLoginAndWatch: (index: 'comment' | 'document', count: number) => void
}

export const CommentCard = ({ comment, commentIndex, userId, commonAmplitudeProps, onClickLoginAndWatch }: Props) => {
  const { t } = useTranslation()

  return (
    <Link
      aria-label="reply card"
      href={userId === NONMEMBER_ID ? '#' : `/community/${comment.community.communityId}#comment-${comment.comment.id}`}
      key={comment.comment.id}
      onClick={() => {
        postAmplitude('click_activity_contents_in_doctor_detail', {
          ...commonAmplitudeProps,
          index: 'comment',
          seq: commentIndex,
        })
      }}
      className="relative flex-none w-[220px] min-h-[220px] flex flex-col p-4 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 no-underline hover:border-gray-300 hover:shadow-sm transition-all duration-200 group"
    >
      {userId === NONMEMBER_ID ? (
        <>
          <img
            src="/images/dummy_community_activity.png"
            alt=""
            className="absolute inset-0 w-full h-full object-cover rounded-xl opacity-30"
          />
          <button
            id={generateTagId('doctor', 'page', 'btn', 'login', 'reply')}
            aria-label="login button"
            onClick={(e) => {
              e.stopPropagation()
              sendAirbridgeEvent(AirbridgeEvents.SIGN_UP_BUTTON_CLICK_IN_WEB, {})
              onClickLoginAndWatch('comment', comment.community.commentCount)
            }}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 px-4 py-2 text-orange-500 bg-white text-sm font-medium border border-orange-500 rounded-lg cursor-pointer hover:bg-orange-50 transition-colors"
          >
            {t('pages.doctorDetail.loginAndWatch')}
          </button>
        </>
      ) : (
        <>
          <div className="flex-1 space-y-3">
            <div
              aria-label="question section"
              className="text-sm leading-relaxed"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              <span className="text-base font-semibold text-gray-900">Q. </span>
              <span className="text-gray-800">{comment.community.contents}</span>
            </div>
            <div
              aria-label="answer section"
              className="text-sm leading-relaxed"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                maxHeight: '5.6em',
              }}
            >
              <span className="text-base font-semibold text-orange-500">A. </span>
              <span className="text-gray-800">{comment.comment.contents}</span>
            </div>
          </div>
          <div className="mt-auto pt-3 border-t border-gray-100">
            <CommunityStatsBar
              helpfulCount={comment.community.helpfulCount}
              commentCount={comment.community.commentCount}
              viewCount={comment.community.viewCount}
            />
          </div>
        </>
      )}
    </Link>
  )
}
